import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "../data/site";
import { useReveal } from "../hooks/useReveal";
import FlyWords from "./FlyWords";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Media Wall",
  "Accent Wall",
  "Entertainment Center",
  "Feature Wall",
  "Slat Wall",
  "LED Lighting",
  "Stone Feature",
  "Custom Shelving",
  "Other",
];

export default function Contact() {
  const ref = useReveal();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please complete name, email, phone and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data) ? err.response.data[0]?.msg : null) ||
        "Something went wrong. Please call us instead.";
      toast.error(typeof detail === "string" ? detail : "Could not submit form.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section bg-[#050505] relative"
      ref={ref}
    >
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5 reveal">
          <div className="overline mb-5">Start your project</div>
          <FlyWords
            as="h2"
            className="font-display text-4xl md:text-5xl lg:text-6xl text-white font-light leading-tight"
            segments={[
              { text: "Let\u2019s build something" },
              { type: "break" },
              { text: "unforgettable.", className: "italic text-[#D4AF37]" },
            ]}
          />
          <p className="mt-6 text-[#A1A1AA] text-base md:text-lg leading-relaxed">
            Tell us about your space and what you have in mind. Rob will reply
            personally — usually within one business day.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={`tel:${SITE.phoneDigits}`}
              data-testid="contact-phone-link"
              className="flex items-center gap-4 group"
            >
              <span className="h-11 w-11 rounded-full border border-[#27272A] flex items-center justify-center text-[#D4AF37] group-hover:border-[#D4AF37] transition">
                <Phone className="h-5 w-5" />
              </span>
              <span>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#A1A1AA]">
                  Call
                </div>
                <div className="text-white text-lg link-gold">{SITE.phone}</div>
              </span>
            </a>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-4 group"
            >
              <span className="h-11 w-11 rounded-full border border-[#27272A] flex items-center justify-center text-[#D4AF37] group-hover:border-[#D4AF37] transition">
                <Mail className="h-5 w-5" />
              </span>
              <span>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#A1A1AA]">
                  Email
                </div>
                <div className="text-white text-lg link-gold break-all">
                  {SITE.email}
                </div>
              </span>
            </a>
            <div className="flex items-center gap-4">
              <span className="h-11 w-11 rounded-full border border-[#27272A] flex items-center justify-center text-[#D4AF37]">
                <MapPin className="h-5 w-5" />
              </span>
              <span>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#A1A1AA]">
                  Service area
                </div>
                <div className="text-white text-base">
                  Tampa · Brandon · Riverview · Clearwater · St. Petersburg
                </div>
              </span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 reveal">
          {submitted ? (
            <div
              data-testid="contact-success"
              className="surface p-10 md:p-14 text-center"
            >
              <div className="overline justify-center mb-5" style={{ display: "inline-flex" }}>
                Request received
              </div>
              <h3 className="font-display text-3xl md:text-4xl text-white mb-4">
                Thank you.
              </h3>
              <p className="text-[#A1A1AA] max-w-md mx-auto">
                Rob will reach out personally to schedule a consultation. For
                anything urgent, give us a call at{" "}
                <a
                  className="text-[#D4AF37] link-gold"
                  href={`tel:${SITE.phoneDigits}`}
                >
                  {SITE.phone}
                </a>
                .
              </p>
              <button
                data-testid="contact-send-another"
                className="btn-ghost mt-8"
                onClick={() => setSubmitted(false)}
              >
                Send another request
              </button>
            </div>
          ) : (
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="surface p-6 md:p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                <div>
                  <label className="block text-[11px] tracking-[0.22em] uppercase text-[#A1A1AA] mb-2">
                    Full name
                  </label>
                  <input
                    data-testid="contact-input-name"
                    type="text"
                    className="input-dark"
                    placeholder="Jane Doe"
                    value={form.name}
                    onChange={update("name")}
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] tracking-[0.22em] uppercase text-[#A1A1AA] mb-2">
                    Phone
                  </label>
                  <input
                    data-testid="contact-input-phone"
                    type="tel"
                    className="input-dark"
                    placeholder="(813) 555-0114"
                    value={form.phone}
                    onChange={update("phone")}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] tracking-[0.22em] uppercase text-[#A1A1AA] mb-2">
                    Email
                  </label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    className="input-dark"
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={update("email")}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] tracking-[0.22em] uppercase text-[#A1A1AA] mb-2">
                    Project type
                  </label>
                  <select
                    data-testid="contact-input-project-type"
                    className="input-dark"
                    value={form.project_type}
                    onChange={update("project_type")}
                  >
                    <option value="">Select a project type (optional)</option>
                    {PROJECT_TYPES.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] tracking-[0.22em] uppercase text-[#A1A1AA] mb-2">
                    Tell us about your space
                  </label>
                  <textarea
                    data-testid="contact-input-message"
                    className="input-dark"
                    placeholder="Room dimensions, style preferences, materials, budget range — anything that helps us understand the vision."
                    value={form.message}
                    onChange={update("message")}
                    required
                  />
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-[#A1A1AA]">
                  We respect your privacy. We&apos;ll only use this to reply to
                  your request.
                </p>
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  className="btn-gold w-full sm:w-auto"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send request
                      <Send className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
